import { createWorker, PSM } from 'tesseract.js';
import { GRADES, normalizeGrade, isExcludedLabel } from './gradePoints';

// Characters that appear in SOLS grade tokens (A+ … E, R, DX/EX) and in the
// special labels we must still recognise so they can be excluded rather than
// misread as grades (IP, S, U, NC, NV, SE, PAS, FAIL, EL001, AEGROTAT …).
const CHAR_WHITELIST = 'ABCDEFGILNOPRSTUVX01+';

// Screenshots of the SOLS grade column are often tiny; upscale small images
// so glyphs are large enough for Tesseract to read reliably.
const TARGET_MAX_DIMENSION = 1600;
const MAX_UPSCALE = 5;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = url;
  });

// Otsu's method over histogram[0..upperBound]: pick the threshold that best
// separates the pixels into two classes.
const otsuThreshold = (histogram, upperBound = 255) => {
  let totalPixels = 0;
  let sum = 0;
  for (let i = 0; i <= upperBound; i++) {
    totalPixels += histogram[i];
    sum += i * histogram[i];
  }

  let sumBackground = 0;
  let weightBackground = 0;
  let maxVariance = 0;
  let threshold = Math.floor(upperBound / 2);

  for (let i = 0; i <= upperBound; i++) {
    weightBackground += histogram[i];
    if (weightBackground === 0) continue;
    const weightForeground = totalPixels - weightBackground;
    if (weightForeground === 0) break;

    sumBackground += i * histogram[i];
    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sum - sumBackground) / weightForeground;
    const betweenVariance =
      weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

    if (betweenVariance > maxVariance) {
      maxVariance = betweenVariance;
      threshold = i;
    }
  }

  return threshold;
};

// Find a threshold that isolates the dark text. The SOLS table has THREE
// luminance classes (white rows, tinted rows, dark text), so a single Otsu
// pass can split the two backgrounds instead of background vs text. When the
// "dark" side ends up implausibly large for text, re-run Otsu on just the
// darker pixels until the text class is a plausible minority.
const findTextThreshold = (histogram, totalPixels) => {
  const maxTextFraction = 0.35;
  let threshold = otsuThreshold(histogram);

  for (let pass = 0; pass < 3; pass++) {
    let darkPixels = 0;
    for (let i = 0; i <= threshold; i++) darkPixels += histogram[i];
    if (darkPixels <= totalPixels * maxTextFraction || darkPixels === 0) break;
    threshold = otsuThreshold(histogram, threshold);
  }

  return threshold;
};

// Upscale, grayscale, and binarize the uploaded screenshot so the alternating
// row colours of the SOLS table become plain black text on white.
export const preprocessImage = async (file) => {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const maxDim = Math.max(img.width, img.height);
    const scale = maxDim >= TARGET_MAX_DIMENSION
      ? 1
      : Math.min(MAX_UPSCALE, TARGET_MAX_DIMENSION / maxDim);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    const grays = new Uint8Array(data.length / 4);
    let luminanceSum = 0;
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      grays[p] = gray;
      luminanceSum += gray;
    }

    // Dark-theme screenshots have light text on a dark background; flip them
    // first so the rest of the pipeline always sees dark text on light.
    if (luminanceSum / grays.length < 128) {
      for (let p = 0; p < grays.length; p++) grays[p] = 255 - grays[p];
    }

    const histogram = new Array(256).fill(0);
    for (let p = 0; p < grays.length; p++) histogram[grays[p]]++;

    const threshold = findTextThreshold(histogram, grays.length);

    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const value = grays[p] <= threshold ? 0 : 255;
      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
      data[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Count grade tokens in OCR output. Only exact (normalized) matches count,
// and special labels such as IP, S, or FAIL are skipped.
export const extractGradesFromText = (rawText) => {
  const validGrades = GRADES.map(g => g.letter);
  const counts = {};
  validGrades.forEach(grade => (counts[grade] = 0));

  const tokens = rawText.split(/[\s,|;:]+/).filter(token => token.length > 0);
  let total = 0;

  tokens.forEach(token => {
    const cleaned = token.toUpperCase();
    if (isExcludedLabel(cleaned)) return;
    const canonical = normalizeGrade(cleaned);
    if (validGrades.includes(canonical)) {
      counts[canonical] += 1;
      total += 1;
    }
  });

  return { counts, total };
};

// Run OCR over the uploaded screenshot and return grade counts. Tries the
// preprocessed image with settings tuned for the SOLS grade column first,
// then falls back to progressively more generic passes.
export const recognizeGrades = async (file, onProgress) => {
  const worker = await createWorker('eng', 1, {
    logger: (m) => {
      if (m.status === 'recognizing text' && typeof onProgress === 'function') {
        onProgress(m.progress);
      }
    },
  });

  try {
    let preprocessed = null;
    try {
      preprocessed = await preprocessImage(file);
    } catch (error) {
      console.warn('Image preprocessing failed, using original image:', error);
    }

    const attempts = [];
    if (preprocessed) {
      attempts.push({
        image: preprocessed,
        parameters: {
          tessedit_char_whitelist: CHAR_WHITELIST,
          tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
        },
      });
    }
    attempts.push({
      image: file,
      parameters: {
        tessedit_char_whitelist: CHAR_WHITELIST,
        tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
      },
    });
    attempts.push({
      image: file,
      parameters: {
        tessedit_char_whitelist: '',
        tessedit_pageseg_mode: PSM.AUTO,
      },
    });

    for (const attempt of attempts) {
      await worker.setParameters(attempt.parameters);
      const { data } = await worker.recognize(attempt.image);
      console.log('OCR text:', data.text);
      const result = extractGradesFromText(data.text);
      if (result.total > 0) {
        return result;
      }
    }

    return extractGradesFromText('');
  } finally {
    await worker.terminate();
  }
};
